/**
 * state/chat/conv-actions.ts — operator-driven mutations of the roster:
 * create a conv, rename it, retune its model/effort/member, archive it,
 * pick the active one.
 *
 * V86x is the rule that shapes all of it: NEVER inject a synthetic
 * message into a conv. New agents arrive as empty convs and introduce
 * themselves on their first real turn.
 */

import { pickCoordinatorConv } from '~/lib/conv-state';
import { state, setState } from './store';
import { saveConvMeta, saveArchivedConvs, saveLastActiveConv } from './persistence';
import { ONBOARDING_CONV_ID, isFixedAgentConv, type AgentType, type ConvMeta } from './types';
import { produce } from 'solid-js/store';

function nextAgentId(): string {
  const used = new Set(Object.values(state.convMeta).map((m) => m.agentId));
  for (let i = 1; i < 1000; i += 1) {
    const id = 'A' + String(i).padStart(3, '0');
    if (!used.has(id)) return id;
  }
  return 'A???';
}

export function ensureConvMeta(convId: string, init: Partial<ConvMeta> = {}): ConvMeta {
  const existing = state.convMeta[convId];
  if (existing) return existing;
  const meta: ConvMeta = {
    agentId: init.agentId ?? nextAgentId(),
    model: init.model ?? 'auto',
    effort: init.effort ?? 'default',
    client: init.client ?? 'claude-code',
    type: (init.type ?? 'custom') as AgentType,
    title: init.title ?? '',
    member: init.member,
    location: init.location ?? { type: 'local', host: 'this machine' },
  };
  setState('convMeta', convId, meta);
  saveConvMeta();
  return meta;
}

export function setActiveConv(conv: string | null): void {
  setState('activeConv', conv);
  // V107.17 — persist per-cluster so a reload lands on the same agent.
  saveLastActiveConv(conv);
}

/**
 * Seed the synthetic Architect conversation (V46 / V78b, ATM20).
 * Idempotent. Creates the empty slot only — the rail card needs it to
 * exist (V82) but the agent introduces itself organically on the first
 * dispatch (the bootstrap brief rides along as a context_doc).
 */
export function seedOnboardingConv(): void {
  if (state.convMap[ONBOARDING_CONV_ID]) return;
  setState('convMap', ONBOARDING_CONV_ID, []);
  ensureConvMeta(ONBOARDING_CONV_ID, {
    // V107.12 — renamed from 'Coordinator': distinctive from the
    // transient roadmap-architect (spawned per Run All pass) and it
    // reinforces that THIS is the always-on master that owns the
    // project. The conv id is unchanged.
    title: 'Architect',
    type: 'custom',
    location: { type: 'local', host: 'this machine' },
  });
  if (!state.activeConv) setState('activeConv', ONBOARDING_CONV_ID);
}

/** True once the Architect conv received a real user message.
 *  Gates the bootstrap-brief attachment (first dispatch only) and keeps
 *  the rail card visible after initiatives appear. */
export function onboardingHasUserMessages(): boolean {
  const list = state.convMap[ONBOARDING_CONV_ID];
  if (!list || list.length === 0) return false;
  return list.some((m) => m.kind === 'user');
}

/**
 * Create an empty conversation with metadata and select it. Used by the
 * chat-rail `+` (ATM7 — binds a `developer` member draft) and story runs.
 * Slug mirrors V79's `newConvSlugFromScope`: scope-encoded for custom
 * agents, type+timestamp for services. Returns the slug so the caller
 * can focus the composer.
 */
export function createConv(opts: {
  type: AgentType;
  title: string;
  model: string;
  effort?: string;
  /** agent-team (ATM7) — bind the new conv to a roster member. */
  member?: string;
  scope?: { module?: string | null; taskId?: string | null };
}): string {
  const stamp = new Date().toISOString().slice(5, 16).replace(/[:T-]/g, '').toLowerCase();
  let slug: string;
  if (opts.type === 'custom') {
    const tid = opts.scope?.taskId?.trim();
    const mod = opts.scope?.module?.trim();
    if (tid) slug = `${(mod || 'general')}-${tid.toLowerCase()}-${stamp}`;
    else if (mod) slug = `${mod}-${stamp}`;
    else slug = `general-${stamp}`;
  } else {
    slug = `${opts.type}-${Date.now().toString(36).slice(-5)}`;
  }
  if (!state.convMap[slug]) setState('convMap', slug, []);
  ensureConvMeta(slug, {
    type: opts.type,
    title: opts.title,
    model: opts.model,
    effort: opts.effort,
    member: opts.member,
  });
  // V107.30 — setActiveConv (not raw setState) so the slug is persisted;
  // otherwise a reload right after creating an agent dropped the
  // selection back to Master.
  setActiveConv(slug);
  return slug;
}

/**
 * V87 — Spawn a fresh agent + conv for a story run. Always a brand-new
 * slug: the operator's contract is "play = new agent, new context,
 * isolated cancel domain".
 */
export function createStoryConv(opts: { initiativeId: string; initiativeTitle: string }): string {
  const stamp = Date.now().toString(36);
  const slug = `story-${opts.initiativeId}-${stamp}`;
  if (!state.convMap[slug]) setState('convMap', slug, []);
  ensureConvMeta(slug, {
    type: 'custom',
    title: opts.initiativeTitle,
    location: { type: 'local', host: 'this machine' },
  });
  setState('activeConv', slug);
  return slug;
}

export function setConvTitle(conv: string, title: string): void {
  ensureConvMeta(conv);
  setState('convMeta', conv, 'title', title);
  saveConvMeta();
}

/** agent-team (ATM7) — set the conv's model live. Every turn is a fresh
 *  `claude -p`, so the change applies from the NEXT dispatch.
 *  ATM14g — a retune here lasts until the member card's engine is saved
 *  again, at which point the daemon stamps the card's value back over it
 *  (and tells every open tab). Nothing to timestamp on this side. */
export function setConvModel(conv: string, model: string): void {
  ensureConvMeta(conv);
  setState('convMeta', conv, 'model', model);
  saveConvMeta();
}

/** ES-2 — switch THIS conv's code client (and claude-code backend) from
 *  the chat header. The whole engine chain re-seeds (engine-chain
 *  `selectClient`), so a Codex conv never inherits an Anthropic model id.
 *  Applies from the next message; the daemon marks the switch in the
 *  thread and gives that turn the full briefing. */
export function setConvEngine(
  conv: string,
  next: { client: string; provider: string; model: string; effort: string },
): void {
  ensureConvMeta(conv);
  setState('convMeta', conv, 'client', next.client);
  setState('convMeta', conv, 'provider', next.provider);
  setState('convMeta', conv, 'model', next.model || 'auto');
  setState('convMeta', conv, 'effort', next.effort || 'default');
  setState('convMeta', conv, 'enginePinned', true);
  saveConvMeta();
}

/** agent-team (ATM7) — reasoning depth; same next-turn semantics. */
export function setConvEffort(conv: string, effort: string): void {
  ensureConvMeta(conv);
  setState('convMeta', conv, 'effort', effort);
  saveConvMeta();
}

/** agent-team (ATM7) — bind a conv to a roster member (BT-5: the team
 *  picker, right after creating it), setting the full engine (client/provider/model/effort)
 *  and the title when it still matches the previous member's name. The
 *  whole engine travels: rebinding to a Codex member must flip the header's
 *  client badge and model catalog too, not just model/effort. */
export function setConvMember(
  conv: string,
  member: string,
  defaults?: { client?: string; provider?: string; model?: string; effort?: string; title?: string },
): void {
  ensureConvMeta(conv);
  setState('convMeta', conv, 'member', member);
  // Engine dials compare against `undefined`, not truthiness: the empty model
  // id is a REAL value ("Default (CLI config)", the first row of the codex /
  // gemini / muse catalogs), and dropping it left the previous member's
  // Anthropic id on a Codex-bound conv. `title` keeps the truthy check — an
  // empty title must never wipe one the operator typed.
  if (defaults?.client !== undefined) setState('convMeta', conv, 'client', defaults.client);
  if (defaults?.provider !== undefined) setState('convMeta', conv, 'provider', defaults.provider);
  if (defaults?.model !== undefined) setState('convMeta', conv, 'model', defaults.model);
  if (defaults?.effort !== undefined) setState('convMeta', conv, 'effort', defaults.effort);
  if (defaults?.title) setState('convMeta', conv, 'title', defaults.title);
  saveConvMeta();
}

export function archiveConv(conv: string): void {
  // V82/V107.12 — the two fixed system agents are never archivable;
  // a stray click must not strand the operator without a default chat.
  if (isFixedAgentConv(conv)) return;
  setState('archivedConvs', conv, true);
  saveArchivedConvs();
}

export function unarchiveConv(conv: string): void {
  // SDEL1 — a Solid store MERGES an object returned from a setter, so
  // rebuilding without the key deleted nothing; `produce` + delete does.
  setState('archivedConvs', produce((d) => { delete d[conv]; }));
  saveArchivedConvs();
}

/**
 * V106 — the most-recent non-archived Orchestrator conv, or null.
 * Consumed by the projects rail's per-row "architect active" dot.
 *
 * The ranking itself lives in `~/lib/conv-state` so this answer, the
 * chat strip's STOP button and the roadmap's Run All button cannot
 * drift apart again.
 */
export function findActiveArchitectConv(): string | null {
  // BT-19 — the Orchestrator is retired: "a run is in progress" means the
  // Architect is working or waiting on a delegate, not that some old
  // `roadmap-architect-*` conv still exists.
  const candidates = Object.entries(state.convMeta).map(([conv, meta]) => ({
    conv,
    member: meta.member ?? null,
    archived: !!state.archivedConvs[conv],
  }));
  const conv = pickCoordinatorConv(candidates);
  if (!conv) return null;
  const s = state.convs[conv];
  return s && (s.live || s.coordinating || s.current_action) ? conv : null;
}

